<template>
  <DSButton
    v-ds-tooltip="tooltip"
    :class="DS_COPY_BUTTON_CLASSES.Root"
    :variant="props.variant"
    :size="props.size"
    :disabled="isDisabled"
    :tabindex="props.tabindex"
    :aria-label="buttonLabel"
    :aria-describedby="props.ariaDescribedBy"
    :full-width="props.fullWidth"
    :align="props.align"
    :color-palette="props.colorPalette"
    :prefix-icon="buttonIcon"
    type="button"
    data-cy="ds-copy-button"
    @click="onClick"
  />
</template>

<script setup lang="ts">
import { toRef } from "vue";
import DSButton from "@/src/design_system/button_and_controls/button/components/DSButton.vue";
import { DS_BUTTON_SIZE, DS_BUTTON_VARIANT } from "@/src/design_system/button_and_controls/button/types/props";
import { useDSCopyButton } from "@/src/design_system/button_and_controls/copy_button/composables/useDSCopyButton";
import { DS_COPY_BUTTON_CLASSES } from "@/src/design_system/button_and_controls/copy_button/constants/classes";
import type { DSCopyButtonEmits, DSCopyButtonProps } from "@/src/design_system/button_and_controls/copy_button/types";
import { vDsTooltip } from "@/src/design_system/primitives/tooltip/directives/vDsTooltip";

// Keep the local String visible to Vue's runtime prop compiler when resolving the inherited Pick type.
type DSCopyButtonRuntimeProps = DSCopyButtonProps & { tooltip?: string };

const props = withDefaults(defineProps<DSCopyButtonRuntimeProps>(), {
  variant: DS_BUTTON_VARIANT.Primary,
  size: DS_BUTTON_SIZE.Medium,
  disabled: false,
  fullWidth: false,
  tooltip: undefined,
});

const emit = defineEmits<DSCopyButtonEmits>();

const { buttonIcon, buttonLabel, copyValue, isDisabled, tooltip } = useDSCopyButton({
  value: toRef(props, "value"),
  disabled: toRef(props, "disabled"),
  ariaLabel: toRef(props, "ariaLabel"),
  tooltip: toRef(props, "tooltip"),
});

const onClick = (event: MouseEvent) => {
  emit("click", event);
  void copyValue();
};
</script>
